import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useState } from 'react';

import { configureStorageVars } from '@app/common/StorageConfig';

import { Flags } from './flagTypes';

export const STORAGE_FEATURE_FLAG_KEY = 'STORAGE_FEATURE_FLAG';
export const PREVIOUS_FEATURE_FLAG_KEY = 'PREVIOUS_FEATURE_FLAG';

const getStorageVars = <T>(
  previousKey: string,
  storageKey: string,
  defaultVars: Record<string, T>,
  shouldResetIfChanged: boolean,
): Record<string, T> => {
  if (typeof localStorage === 'undefined') {
    return defaultVars;
  }

  configureStorageVars(previousKey, storageKey, defaultVars, shouldResetIfChanged);

  const storageVarString = localStorage.getItem(storageKey);
  if (!storageVarString) {
    return defaultVars;
  }

  const storageVar: Record<string, T> = JSON.parse(storageVarString);
  return storageVar;
};

// TODO: Used by env var config, should be moved to there
export const getVarProvider = <T>(
  previousKey: string,
  storageKey: string,
  originalVars: Record<string, T>,
): Record<string, T> => {
  return getStorageVars(previousKey, storageKey, originalVars, true);
};

const extractFlags = (flagsKeys: (keyof Flags)[], launchDarklyFlags: Flags): Partial<Flags> => {
  return flagsKeys.reduce((acc, key) => {
    acc[key] = launchDarklyFlags[key as keyof Flags] ?? false;
    return acc;
  }, {} as Partial<Flags>);
};

export const getInitialFlags = (
  previousKey: string,
  storageKey: string,
  flagsKeys: (keyof Flags)[],
  launchDarklyFlags: Flags,
): Partial<Flags> => {
  const extractedUsedFlagsObject = extractFlags(flagsKeys, launchDarklyFlags);
  return getStorageVars(previousKey, storageKey, extractedUsedFlagsObject, false);
};

export const hardcodedFeatureFlags: (keyof Flags)[] = [
  'forceLocalhostRedirectUrl', // If true http://localhost:3000 will be used as the redirect URL even in a build (useful for testing static builds locally)
  'rolloutTransactionViewer', //Transaction viewer has an old (transactionLog) and new (transactionViewer) screen. If true, the new one will be used
  'enableSuspendedPouches',
  'rolloutCtBackNavigation',
  'rolloutMonthlyBalancePrinting',
  'rolloutEnhancedBalanceReporting',
  'enableAdditionalKnownVarianceValidation',
  'rolloutPouchEnhancements',
  'rolloutBackButtonVisibility',
  'rolloutPouchHistoryEnhancements',
  'configureCtBoUrl',
  'rolloutWeeklyMonthlyBalanceEnhancements',
];

export const getOriginalFeatureFlags = (launchDarklyFlags: Flags) => {
  return extractFlags(hardcodedFeatureFlags, launchDarklyFlags);
};

// used only to get local feature flags
export const getLocalFeatureFlag = (featureFlag: string) => {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  const storageVarString = localStorage.getItem(STORAGE_FEATURE_FLAG_KEY);
  if (!storageVarString) {
    return false;
  }

  const storageVar: Record<string, boolean> = JSON.parse(storageVarString);
  return storageVar[featureFlag];
};

const mergeFlags = (
  LDflags: Partial<Flags>,
  hardcodedFlags: Partial<Flags>,
  localStorageFlags: Partial<Flags>,
): Partial<Flags> => {
  const merged = { ...LDflags }; // default to LD flags

  // overwrite with hardcoded flags
  for (const key in hardcodedFlags) {
    if (hardcodedFlags.hasOwnProperty(key)) {
      merged[key as keyof Flags] = hardcodedFlags[key as keyof Flags];
    }
  }

  // Overwrite with local storage flags if they exist
  for (const key in localStorageFlags) {
    if (localStorageFlags.hasOwnProperty(key)) {
      merged[key as keyof Flags] = localStorageFlags[key as keyof Flags];
    }
  }
  return merged;
};

// used to get all feature flags, from launch darkly & local
export const useFeatureFlags = () => {
  const LDflags = useFlags<Flags>();

  const initialFeatureFlags = getInitialFlags(
    PREVIOUS_FEATURE_FLAG_KEY,
    STORAGE_FEATURE_FLAG_KEY,
    hardcodedFeatureFlags,
    LDflags,
  );

  const localStorageFlags = JSON.parse(localStorage.getItem(STORAGE_FEATURE_FLAG_KEY) || '{}');

  const [flags, setFlags] = useState(() =>
    mergeFlags(LDflags, initialFeatureFlags, localStorageFlags),
  );

  const handleLocalStorageChange = useCallback(
    (event: StorageEvent) => {
      if (event.key === STORAGE_FEATURE_FLAG_KEY) {
        const updatedLocalStorageFlags = JSON.parse(
          localStorage.getItem(STORAGE_FEATURE_FLAG_KEY) || '{}',
        );
        setFlags(mergeFlags(LDflags, initialFeatureFlags, updatedLocalStorageFlags));
      }
    },
    [LDflags],
  );

  useEffect(() => {
    window.addEventListener('storage', handleLocalStorageChange);
    return () => {
      window.removeEventListener('storage', handleLocalStorageChange);
    };
  }, [handleLocalStorageChange]);

  useEffect(() => {
    setFlags(mergeFlags(LDflags, initialFeatureFlags, localStorageFlags));
  }, [LDflags]);
  return flags;
};
